// src/Pages/ApiKeys/ApiKeyList.tsx

import type React from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@tremor/react";
import {
  KeyIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
} from "@heroicons/react/solid";
import { formatDate } from "@/utils";
import type { ApiKey } from "@/types/apiKeys";
import { useNavigate } from "react-router-dom";

interface ApiKeyListProps {
  keys: ApiKey[];
  onToggleStatus: (id: number, isActive: boolean) => void;
  onRevoke: (id: number) => void;
  loading?: boolean;
}

const ApiKeyList: React.FC<ApiKeyListProps> = ({
  keys,
  onToggleStatus,
  onRevoke,
  loading = false,
}) => {
  const navigate = useNavigate();

  const handleOrgClick = (orgId: number) => {
    navigate(`/org/${orgId}?tab=api-keys`);
  };

  const getLastUsedDisplay = (lastUsedAt?: string) => {
    if (!lastUsedAt) return "Ποτέ";
    
    const lastUsedDate = new Date(lastUsedAt);
    const now = new Date();
    const diffMs = now.getTime() - lastUsedDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return `Πριν ${diffMinutes} λεπτά`;
      }
      return `Πριν ${diffHours} ώρες`;
    } else if (diffDays === 1) {
      return "Χθες";
    } else if (diffDays < 7) {
      return `Πριν ${diffDays} ημέρες`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Πριν ${weeks} εβδομάδ${weeks === 1 ? "α" : "ες"}`;
    } else {
      return formatDate(lastUsedAt, { simple: true, withTime: false });
    }
  };

  const getUsageIndicator = (lastUsedAt?: string) => {
    if (!lastUsedAt) {
      const createdDate = keys.find(k => !k.last_used_at)?.created_at;
      if (createdDate) {
        const daysSinceCreation = Math.floor(
          (new Date().getTime() - new Date(createdDate).getTime()) / (1000 * 60 * 60 * 24)
        );
        if (daysSinceCreation > 30) {
          return { color: "yellow", tooltip: "Δεν έχει χρησιμοποιηθεί ποτέ (>30 ημέρες)" };
        }
      }
      return { color: "gray", tooltip: "Δεν έχει χρησιμοποιηθεί ακόμα" };
    }
    
    const hoursSinceUse = Math.floor(
      (new Date().getTime() - new Date(lastUsedAt).getTime()) / (1000 * 60 * 60)
    );
    
    if (hoursSinceUse < 24) {
      return { color: "green", tooltip: "Πρόσφατη χρήση (τελευταίες 24 ώρες)" };
    }
    return { color: "blue", tooltip: getLastUsedDisplay(lastUsedAt) };
  };

  const parseAllowedProducts = (allowedProducts?: string) => {
    if (!allowedProducts || allowedProducts === "null") return null;
    try {
      return JSON.parse(allowedProducts) as number[];
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">Φόρτωση API keys...</div>
        </CardContent>
      </Card>
    );
  }

  if (keys.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            <KeyIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Δεν υπάρχουν API keys</p>
            <p className="text-sm mt-2">Δημιουργήστε το πρώτο API key για να ξεκινήσετε</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Όνομα</TableHead>
              <TableHead>Οργανισμός</TableHead>
              <TableHead>Key Prefix</TableHead>
              <TableHead>Κατάσταση</TableHead>
              <TableHead>Τελευταία Χρήση</TableHead>
              <TableHead>Προϊόντα</TableHead>
              <TableHead>Δημιουργήθηκε</TableHead>
              <TableHead>Από</TableHead>
              <TableHead className="text-right">Ενέργειες</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((key) => {
              const usageIndicator = getUsageIndicator(key.last_used_at);
              const allowedProducts = parseAllowedProducts(key.allowed_products);
              
              return (
                <TableRow key={key.id} className="hover:bg-gray-50">
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <div
                        className={`w-2 h-2 rounded-full bg-${usageIndicator.color}-500`}
                        title={usageIndicator.tooltip}
                      />
                      <span className="font-medium">{key.name}</span>
                      {key.description && (
                        <span className="text-xs text-gray-500" title={key.description}>
                          (i)
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => handleOrgClick(key.org_id)}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {key.org_name}
                    </button>
                  </TableCell>
                  <TableCell>
                    <code className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                      {key.key_prefix}...
                    </code>
                  </TableCell>
                  <TableCell>
                    <Badge color={key.is_active ? "green" : "gray"}>
                      {key.is_active ? "Ενεργό" : "Ανενεργό"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className={`text-sm ${!key.last_used_at ? "text-gray-400" : ""}`}>
                      {getLastUsedDisplay(key.last_used_at)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {allowedProducts ? (
                      <Badge color="blue">{allowedProducts.length} επιλεγμένα</Badge>
                    ) : (
                      <Badge color="gray">Όλα</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDate(key.created_at, { simple: true, withTime: false })}
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {key.created_by_email}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end space-x-1">
                      <button
                        onClick={() => onToggleStatus(key.id, key.is_active)}
                        className={`p-1.5 rounded hover:bg-gray-100 ${
                          key.is_active ? "text-amber-500" : "text-emerald-500"
                        }`}
                        title={key.is_active ? "Απενεργοποίηση" : "Ενεργοποίηση"}
                      >
                        {key.is_active ? (
                          <PauseIcon className="h-4 w-4" />
                        ) : (
                          <PlayIcon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => onRevoke(key.id)}
                        className="p-1.5 rounded hover:bg-gray-100 text-red-500"
                        title="Διαγραφή API key"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default ApiKeyList;